import { radius } from '@alison/ui';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { KeyboardEvent, ReactNode } from 'react';
import type { CreativeMedia } from '../../lib/workflows.hooks.js';
import { ThumbnailPreview } from '../creatives/ThumbnailPreview.js';
import { ELLIPSIS_SX } from '../creatives/utils.js';
import { AttributeBar, readable, toneOf } from './ScoreVisuals.js';
import { creativeLine, type ScoredCreative } from './scoredBatch.js';

const THUMB_WIDTH = 172;
const THUMB_HEIGHT = 104;
const LABEL_WIDTH = 88;

const CARD_SX = {
  gap: 2.5,
  p: 3,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const BADGE_SX = {
  position: 'absolute',
  top: 8,
  left: 8,
  px: 1.5,
  py: 0.25,
  borderRadius: 1,
  fontSize: 10,
  fontWeight: 700,
  color: 'common.white',
  bgcolor: 'rgba(0, 0, 0, 0.62)',
  pointerEvents: 'none',
};

const RUNS_SX = {
  flexShrink: 0,
  gap: 0.5,
  alignItems: 'center',
  px: 1.5,
  py: 0.5,
  borderRadius: `${radius.pill}px`,
  color: 'primary.main',
  bgcolor: 'primary.light',
};

export function CreativeScoreCard({
  creative,
  media,
  runs = 1,
  onOpen,
}: {
  creative: ScoredCreative;
  media: CreativeMedia | null;
  runs?: number;
  onOpen?: () => void;
}): ReactNode {
  const theme = useTheme();
  const line = creativeLine(creative);

  return (
    <Stack
      sx={[CARD_SX, onOpen !== undefined && { cursor: 'pointer' }]}
      {...(onOpen === undefined
        ? {}
        : {
            onClick: onOpen,
            onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            },
            role: 'button',
            tabIndex: 0,
            'aria-label': creative.name,
          })}
    >
      <Stack direction="row" sx={{ gap: 2.5, alignItems: 'flex-start' }}>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <ThumbnailPreview
            name={creative.name}
            size={THUMB_HEIGHT}
            width={THUMB_WIDTH}
            thumbnailUrl={media?.url ?? null}
            previewUrl={media?.previewUrl ?? null}
            duration={creative.duration}
          />
          <Typography sx={BADGE_SX}>{creative.creativeType.toUpperCase()}</Typography>
        </Box>

        <Stack sx={{ gap: 0.25, minWidth: 0, flex: 1 }}>
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <Tooltip title={creative.name}>
              <Typography
                sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, ...ELLIPSIS_SX }}
              >
                {creative.name}
              </Typography>
            </Tooltip>
            {runs < 2 ? null : (
              <Stack direction="row" sx={RUNS_SX}>
                <RefreshOutlined sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{runs} runs</Typography>
              </Stack>
            )}
          </Stack>
          {line === '' ? null : <Typography sx={{ color: 'text.muted' }}>{line}</Typography>}
          <Typography
            sx={{
              mt: 1,
              fontSize: 26,
              fontWeight: 500,
              color: toneOf(theme, creative.score),
            }}
          >
            {readable(creative.score)}
          </Typography>
          <Typography sx={{ color: 'text.muted' }}>Overall score</Typography>
        </Stack>
      </Stack>

      <Stack sx={{ gap: 1.25 }}>
        {creative.attributes.map((attribute) => (
          <AttributeBar
            key={attribute.name}
            name={attribute.name}
            percent={attribute.percent}
            labelWidth={LABEL_WIDTH}
          />
        ))}
      </Stack>
    </Stack>
  );
}
